/**
 * <vz-fitment-bar> — progressive enhancement for the fitment context bar,
 * plus the "saved to your garage" toast (VZB-003).
 *
 * The element is server-rendered inside #ProductGridContainer, whose innerHTML
 * Dawn replaces wholesale on every facet change. Each replacement therefore
 * constructs a brand new instance, which is why all the work lives in
 * connectedCallback and there is no one-off init and no MutationObserver.
 *
 * Everything here is enhancement. With JS disabled the bar still names the car
 * and still clears it; these two jobs only make it nicer:
 *
 *   1. Name the engine. The URL carries engine CODES, plus the literal `ALL`,
 *      because codes are what the fits_engine_code metafield holds. A code means
 *      nothing to a shopper, so the human label is recovered from the garage —
 *      but only when the stored car is the one actually being filtered on.
 *      Skipping that check would let a stale garage entry mislabel a link
 *      someone was sent.
 *
 *   2. Clear only the vehicle. Liquid cannot subtract a subset of keys from a
 *      query string, so the no-JS href drops every facet. Here we strip just the
 *      fits_* keys and leave the shopper's price and brand filters standing.
 *
 * THE GARAGE TOAST
 *
 * Hitting Search in the finder saves the car to the garage — that is what
 * makes the PDP "fits / does not fit your …" messages work — but until now it
 * did so silently, and shoppers browsing a different model later were confused
 * by fitment warnings against a car they never knowingly saved. The finder now
 * leaves a one-shot note in sessionStorage; this file reads it once, on the
 * results page, and shows a short toast:
 *
 *     new car        "Saved Land Rover Discovery 2015 to your garage · Undo"
 *     existing car   "Now shopping for Land Rover Discovery 2015"
 *
 *     not saved     "Showing parts for Range Rover Sport. Add year and engine
 *                    to save this car to your garage."   (QA UAE-0016)
 *
 * 2026-10-04: the finder only saves a complete vehicle. A Make + Model search
 * leaves a note with notSaved: true and gets the hint above instead. Names
 * come from the garage element's describe() ("Range Rover Sport 2016"), and
 * the toast copy follows the page language (English / Arabic).
 *
 * Undo removes the car from the garage (both storage keys, same as the garage
 * drawer's own Remove) but leaves the results on screen — they searched for it,
 * they just don't want it remembered. It is a plain script, not a module, so
 * the storage keys are duplicated here rather than imported; they are the
 * inherited keys and must not change (see assets/vz-garage.js).
 */

// Inherited from the Ignite theme. Renaming it silently empties every existing
// customer's garage on migration day — see assets/vz-garage.js.
const VZ_GARAGE_KEY = 'vz_garage';
const VZ_GARAGE_LIST_KEY = 'vz_garage_list';

// Written by vz-vehicle-finder.js just before it navigates here.
const VZ_GARAGE_NOTE_KEY = 'vz_garage_note';

// The store-wide lock query, replayed by snippets/vz-vehicle-lock.liquid. Any
// control that clears the vehicle MUST remove this before navigating, or the
// head script puts the car straight back on the next page.
const VZ_GARAGE_QUERY_KEY = 'vz_garage_query';

function vzUnlockVehicle() {
  try {
    window.localStorage.removeItem(VZ_GARAGE_QUERY_KEY);
    window.localStorage.removeItem(VZ_GARAGE_KEY);
    // VIN fitment (13 Oct 2026): the exact-VIN check goes with the vehicle.
    window.sessionStorage.removeItem('vz_vin_decode');
  } catch {
    /* no storage — nothing was locked in the first place */
  }
}

// Every [data-vz-clear] link on the page (fitment bar "Clear vehicle", empty
// state "Show all parts") lifts the lock as it is followed. Delegated, because
// Dawn re-renders the grid container on every facet change.
document.addEventListener('click', (event) => {
  if (event.target.closest('[data-vz-clear]')) vzUnlockVehicle();
});

// The URL contract. Kept in sync with FILTER_PREFIX in vz-fitment-core.js.
const VZ_FITS_PREFIX = 'filter.p.m.custom.fits_';

const VZ_TOAST_MS = 9000;

class VZFitmentBar extends HTMLElement {
  connectedCallback() {
    this.#nameEngine();
    this.#scopeClearLink();
  }

  #nameEngine() {
    const target = this.querySelector('[data-vz-vehicle]');
    if (!target) return;

    const stored = this.#storedVehicle();
    if (!stored || !stored.engine) return;

    const describesTheSameCar =
      this.#same(stored.make, this.dataset.make) &&
      this.#same(stored.model, this.dataset.model) &&
      this.#same(stored.year, this.dataset.year);

    if (!describesTheSameCar) return;

    target.textContent = `${target.textContent.trim()} ${stored.engine}`.trim();
  }

  #scopeClearLink() {
    const link = this.querySelector('[data-vz-clear]');
    if (!link) return;

    const url = new URL(window.location.href);

    // Snapshot the keys first — deleting while iterating a live URLSearchParams
    // skips entries.
    [...url.searchParams.keys()]
      .filter((key) => key.startsWith(VZ_FITS_PREFIX))
      .forEach((key) => url.searchParams.delete(key));

    // Page 4 of the old result set is meaningless against the new one.
    url.searchParams.delete('page');

    link.href = `${url.pathname}${url.search}`;
  }

  #storedVehicle() {
    try {
      return JSON.parse(window.localStorage.getItem(VZ_GARAGE_KEY) || 'null');
    } catch {
      // Safari in private browsing throws on localStorage access. A shopper with
      // no persistence gets the server-rendered bar, which is already correct.
      return null;
    }
  }

  #same(a, b) {
    return String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
  }
}

if (!customElements.get('vz-fitment-bar')) {
  customElements.define('vz-fitment-bar', VZFitmentBar);
}

/* ------------------------------------------------------------- garage toast -- */

(function vzGarageToast() {
  let note = null;
  try {
    const raw = window.sessionStorage.getItem(VZ_GARAGE_NOTE_KEY);
    if (!raw) return;
    window.sessionStorage.removeItem(VZ_GARAGE_NOTE_KEY);
    note = JSON.parse(raw);
  } catch {
    return;
  }
  if (!note || !note.make || !note.model) return;

  const isAr = (document.documentElement.lang || '').toLowerCase().indexOf('ar') === 0;
  const T = isAr
    ? {
        saved: ['تم حفظ ', ' في سياراتي'],
        now: ['نتسوق الآن لـ ', ''],
        removed: ['تم حذف ', ' من سياراتي'],
        notSaved: ['نعرض القطع المناسبة لـ ', '. أضف سنة الصنع والمحرك لحفظ السيارة في سياراتي.'],
        undo: 'تراجع',
        garage: 'سياراتي',
        dismiss: 'إغلاق',
      }
    : {
        saved: ['Saved ', ' to your garage'],
        now: ['Now shopping for ', ''],
        removed: ['Removed ', ' from your garage'],
        notSaved: ['Showing parts for ', '. Add year and engine to save this car to your garage.'],
        undo: 'Undo',
        garage: 'My garage',
        dismiss: 'Dismiss',
      };

  // Fallback name, used only if the garage element never loads. Normally the
  // name comes from <vz-garage>.describe() so toast and drawer agree.
  let name = (() => {
    const makes = isAr ? { 'Range Rover': 'لاند روفر', Jaguar: 'جاكوار' } : { 'Range Rover': 'Land Rover' };
    return [makes[note.make] || note.make, note.model, note.year].filter(Boolean).join(' ');
  })();

  const resolveName = () => {
    const timeout = new Promise((resolve) => window.setTimeout(resolve, 2000));
    const defined = window.customElements ? window.customElements.whenDefined('vz-garage') : timeout;
    return Promise.race([defined, timeout]).then(() => {
      const garage = document.querySelector('vz-garage');
      if (garage && typeof garage.describe === 'function') {
        const described = garage.describe(note);
        if (described) name = described;
      }
    }).catch(() => {});
  };

  const vehicleKey = (v) => [v.make, v.model, v.year || '', v.engine || ''].join('|');
  const noteKey = vehicleKey({
    make: note.make,
    model: note.model,
    year: note.year ? String(note.year) : '',
    engine: note.engine || '',
  });

  const read = (key) => {
    try {
      return JSON.parse(window.localStorage.getItem(key) || 'null');
    } catch {
      return null;
    }
  };
  const write = (key, value) => {
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage unavailable */
    }
  };

  const undo = () => {
    const list = Array.isArray(read(VZ_GARAGE_LIST_KEY)) ? read(VZ_GARAGE_LIST_KEY) : [];
    write(VZ_GARAGE_LIST_KEY, list.filter((entry) => vehicleKey(entry) !== noteKey));

    const selected = read(VZ_GARAGE_KEY);
    if (selected && vehicleKey(selected) === noteKey) {
      write(VZ_GARAGE_KEY, null);
      write(VZ_GARAGE_QUERY_KEY, null);
    }

    // The drawer and the header button read storage on render; ask them to.
    const garage = document.querySelector('vz-garage');
    if (garage && typeof garage.render === 'function') garage.render();
  };

  const build = () => {
    const toast = document.createElement('div');
    toast.className = 'vz-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const text = document.createElement('p');
    text.className = 'vz-toast__text';

    const strong = document.createElement('strong');
    strong.textContent = name;

    if (note.notSaved) {
      text.append(T.notSaved[0], strong, T.notSaved[1]);
    } else if (note.isNew) {
      text.append(T.saved[0], strong, T.saved[1]);
    } else {
      text.append(T.now[0], strong, T.now[1]);
    }
    toast.appendChild(text);

    const actions = document.createElement('div');
    actions.className = 'vz-toast__actions';

    if (note.isNew && !note.notSaved) {
      const undoButton = document.createElement('button');
      undoButton.type = 'button';
      undoButton.className = 'vz-toast__action';
      undoButton.textContent = T.undo;
      undoButton.addEventListener('click', () => {
        undo();
        text.textContent = '';
        text.append(T.removed[0], strong, T.removed[1]);
        actions.remove();
        window.setTimeout(dismiss, 2500);
      });
      actions.appendChild(undoButton);
    }

    // Nothing was saved on a partial search, so there is no garage to open.
    if (!note.notSaved) {
      const garageButton = document.createElement('button');
      garageButton.type = 'button';
      garageButton.className = 'vz-toast__action vz-toast__action--quiet';
      garageButton.textContent = T.garage;
      // vz-garage-ui.js opens the drawer for any click on [data-vz-garage-open].
      garageButton.setAttribute('data-vz-garage-open', '');
      garageButton.addEventListener('click', dismiss);
      actions.appendChild(garageButton);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'vz-toast__close';
    close.setAttribute('aria-label', T.dismiss);
    close.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">' +
      '<path d="M1 1l12 12M13 1L1 13"/></svg>';
    close.addEventListener('click', dismiss);

    toast.append(actions, close);
    return toast;
  };

  let toast = null;
  let timer = null;

  const dismiss = () => {
    if (!toast) return;
    window.clearTimeout(timer);
    toast.classList.add('is-leaving');
    const remove = () => { if (toast) toast.remove(); toast = null; };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) remove();
    else window.setTimeout(remove, 220);
  };

  const show = () => {
    toast = build();
    document.body.appendChild(toast);
    // Next frame, so the entrance transition has a starting state to run from.
    window.requestAnimationFrame(() => toast && toast.classList.add('is-visible'));
    timer = window.setTimeout(dismiss, VZ_TOAST_MS);

    // Hovering pauses the clock; leaving restarts it. Nobody should have to
    // race the timer to read a sentence.
    toast.addEventListener('mouseenter', () => window.clearTimeout(timer));
    toast.addEventListener('mouseleave', () => { timer = window.setTimeout(dismiss, VZ_TOAST_MS); });
  };

  const start = () => { resolveName().then(show); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();